import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { IInvoiceApplyRequest, IInvoiceDetail, IInvoiceListItem, IInvoiceListParam, IInvoicePreApplyRequest, IInvoiceUploadParam, IUnApplyedInvoiceListItem, IUnApplyedInvoiceListParam } from '../types';
import { ESettlementInvoiceStatus } from '@core/main/types';
import { getHostbaseUrl } from '@core/tools';

const hostUrl = getHostbaseUrl();

/**
 * 众包灵工 - 发票 - 已开票列表查询
 */
export async function queryInvoicedList(params = {} as IInvoiceListParam, showDownload = false) {
  let total = 0;
  let data = [] as IInvoiceListItem[];

  params.dutyType = params.dutyType || 0;
  params.page = params.page || 1;
  params.size = params.size || 12;

  try {
    const { code, context, message } = await Http.get<{ data: IInvoiceListItem[]; total: number }>(
      `${hostUrl}/pay/invoice/page`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IInvoiceListItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;

      data = data.map((item) => ({
        ...item,
        showDownload: showDownload && item.invoiceStatus === ESettlementInvoiceStatus.InvoiceSuccess,
        showAudit: item.invoiceStatus === ESettlementInvoiceStatus.InvoicePending,
        showCancel: item.invoiceStatus === ESettlementInvoiceStatus.InvoiceUnpaid,
        showUpload: item.invoiceStatus === ESettlementInvoiceStatus.InvoiceUnpaid,
      }));
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 众包灵工 - 发票 - 未申请发票列表查询
 */
export async function queryUnApplyedInvoiceList(params = {} as IUnApplyedInvoiceListParam, term?: string) {
  let total = 0;
  let data = [] as IUnApplyedInvoiceListItem[];

  params.dutyType = params.dutyType || 0;

  try {
    const { code, context, message } = await Http.get<{ data: IUnApplyedInvoiceListItem[]; total: number }>(
      `${hostUrl}/pay/invoice/not-apply-order/page`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IUnApplyedInvoiceListItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;

      data = data.map((item) => ({
        ...item,
        term,
      }));
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 众包灵工 - 发票 - 开票列表批量导出
 */
export async function exportInvoicedList(params: IInvoiceListParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/pay/invoice/export`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 众包灵工 - 发票 - 取消待开开票
 * @param invoiceId 发票ID
 * @param dutyType 0: 众包 1: 撮合
 */
export async function cancelInvoicedItem(invoiceId: number, dutyType: 0 | 1) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/invoice/cancel?invoiceId=${invoiceId}&dutyType=${dutyType}`,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 发票 - 未申请发票列表批量导出
 */
export async function exportUnApplyedInvoiceList(params: IUnApplyedInvoiceListParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/pay/invoice/not-apply-order/export`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 众包灵工 - 发票 - 上传发票
 */
export async function uploadInvoiceInfo(params: IInvoiceUploadParam) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/invoice/upload`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 申请开票
 */
export async function applyInvoice(params: IInvoiceApplyRequest): Promise<void> {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/invoice/apply`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 查询发票详情
 * @param invoiceId 发票ID
 * @param dutyType 0: 众包 1: 撮合
 */
export async function queryInvoiceDetail(invoiceId: number, dutyType: 0 | 1): Promise<IInvoiceDetail> {
  try {
    const { code, context, message } = await Http.get<IInvoiceDetail>(
      `${hostUrl}/pay/invoice/detail`,
      {
        invoiceId,
        dutyType,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {} as IInvoiceDetail;
    }

    throw new Error(message || '获取发票详情失败');
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 查询发票表单信息信息
 * @returns 发票表单信息信息
 */
export async function queryInvoiceFormData(params: { orderIds: number[] | string; companyId: number; dutyId: number; dutyType?: 0 | 1 }): Promise<IInvoiceDetail> {
  let newParams = JSON.parse(JSON.stringify(params));

  newParams.dutyType = newParams.dutyType || 0;
  newParams.orderIds = (newParams.orderIds as number[] || []).join(',');

  try {
    const { code, context, message } = await Http.get<IInvoiceDetail>(
      `${hostUrl}/pay/invoice/apply`,
      { ...newParams },
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {} as IInvoiceDetail;
    }

    throw new Error(message || '获取发票详情失败');
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 预开票申请
 */
export async function applyPreInvoice(params: IInvoicePreApplyRequest): Promise<void> {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/invoice/pre-apply`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}
